/**
 * PowerGrid - Reusable table for displaying unexecuted powers
 * Supports multi-select for power reassignment operations
 */
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { MagnifyingGlassIcon, XMarkIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import ElectronicPowerBadge from '../../../shared/ElectronicPowerBadge';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '--';
  return currencyFormatter.format(value);
};

const PowerGrid = ({
  powers = [],
  selectedIds,
  onSelectionChange,
  title,
  emptyMessage = 'No powers found',
  loading = false,
  onQuickAdd,
  quickAddPlaceholder = 'Power # to add...',
  agencyId,
}) => {
  const [sortDirection, setSortDirection] = useState(null); // null | 'asc' | 'desc'
  const [quickAddValue, setQuickAddValue] = useState('');
  const [quickAddError, setQuickAddError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  const sortedPowers = useMemo(() => {
    if (!sortDirection) return powers;
    return [...powers].sort((a, b) => {
      const cmp = a.powerNo.localeCompare(b.powerNo, undefined, { numeric: true });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [powers, sortDirection]);

  const filteredPowers = useMemo(() => {
    if (!quickAddValue.trim()) return sortedPowers;
    const term = quickAddValue.trim().toLowerCase();
    return sortedPowers.filter((p) => p.powerNo.toLowerCase().includes(term));
  }, [sortedPowers, quickAddValue]);

  const handleSortToggle = () => {
    setSortDirection((prev) => {
      if (prev === null) return 'asc';
      if (prev === 'asc') return 'desc';
      return null;
    });
  };

  const allSelected = filteredPowers.length > 0 && filteredPowers.every((p) => selectedIds.has(p.powerNo));
  const someSelected = filteredPowers.some((p) => selectedIds.has(p.powerNo)) && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(filteredPowers.map((p) => p.powerNo)));
    }
  };

  const handleToggle = (powerNo) => {
    const next = new Set(selectedIds);
    if (next.has(powerNo)) {
      next.delete(powerNo);
    } else {
      next.add(powerNo);
    }
    onSelectionChange(next);
  };

  const handleQuickAdd = () => {
    const trimmed = quickAddValue.trim();
    if (!trimmed) return;
    const found = powers.some((p) => p.powerNo === trimmed);
    if (found) {
      onQuickAdd(trimmed);
      setQuickAddValue('');
      setQuickAddError('');
    } else {
      setQuickAddError('Power # not found in this list');
    }
  };

  const handleDropdownSelect = useCallback((powerNo) => {
    onQuickAdd(powerNo);
    setQuickAddValue('');
    setQuickAddError('');
    setShowDropdown(false);
    setHighlightedIndex(-1);
  }, [onQuickAdd]);

  const handleKeyDown = useCallback((e) => {
    if (!onQuickAdd) return;

    if (!showDropdown || filteredPowers.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowDropdown(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      if (e.key === 'Enter') handleQuickAdd();
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredPowers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredPowers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredPowers.length) {
          handleDropdownSelect(filteredPowers[highlightedIndex].powerNo);
        } else {
          handleQuickAdd();
        }
        break;
      case 'Tab':
        if (highlightedIndex >= 0 && highlightedIndex < filteredPowers.length) {
          handleDropdownSelect(filteredPowers[highlightedIndex].powerNo);
        }
        setShowDropdown(false);
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  }, [onQuickAdd, showDropdown, filteredPowers, highlightedIndex, handleDropdownSelect]);

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.children[highlightedIndex];
      if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex]);

  if (loading) {
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        <div className="p-4 space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded" />
              <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-600 rounded" />
              <div className="w-16 h-4 bg-gray-200 dark:bg-gray-600 rounded hidden sm:block" />
              <div className="w-14 h-4 bg-gray-200 dark:bg-gray-600 rounded" />
            </div>
          ))}
          <p className="sr-only">Loading powers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden min-w-0">
      <div className="bg-gray-50 dark:bg-gray-700/50 px-3 sm:px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-baseline justify-between gap-2 min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate min-w-0">{title}</h3>
        <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
          {selectedIds.size > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary border border-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-800">
              {selectedIds.size} selected
            </span>
          )}
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {powers.length} total
          </span>
        </div>
      </div>

      {(onQuickAdd || powers.length > 0) && (
        <div className="px-3 sm:px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-visible relative z-10">
          <div className="relative min-w-0">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={quickAddValue}
                onChange={(e) => {
                  setQuickAddValue(e.target.value);
                  setQuickAddError('');
                  if (onQuickAdd) {
                    setShowDropdown(true);
                    setHighlightedIndex(-1);
                  }
                }}
                onKeyDown={onQuickAdd ? handleKeyDown : undefined}
                onFocus={() => {
                  if (onQuickAdd && quickAddValue.trim()) {
                    setShowDropdown(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowDropdown(false);
                    setHighlightedIndex(-1);
                  }, 150);
                }}
                placeholder={onQuickAdd ? quickAddPlaceholder : 'Filter by power #...'}
                disabled={loading}
                aria-label={onQuickAdd ? 'Add or filter power number' : 'Filter powers by number'}
                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50"
              />
              {quickAddValue && !onQuickAdd && (
                <button
                  onClick={() => { setQuickAddValue(''); setQuickAddError(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  aria-label="Clear filter"
                >
                  <XMarkIcon className="h-3.5 w-3.5 text-gray-400" />
                </button>
              )}
            </div>
            {onQuickAdd && showDropdown && quickAddValue.trim() && filteredPowers.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
              >
                {filteredPowers.map((p, index) => (
                  <button
                    key={p.powerNo}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleDropdownSelect(p.powerNo)}
                    className={`w-full px-4 py-2 text-left text-sm ${
                      index === highlightedIndex
                        ? 'bg-primary/10 dark:bg-primary/20'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="font-mono text-gray-900 dark:text-white">{p.powerNo}</span>
                    <span className="ml-2 text-gray-500 dark:text-gray-400">
                      {p.powerTypeDescription || p.powerTypeCd}
                    </span>
                    <span className="ml-2 text-gray-500 dark:text-gray-400">
                      {formatCurrency(p.powerAmountValue)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {quickAddError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{quickAddError}</p>
          )}
        </div>
      )}

      {powers.length === 0 ? (
        <div className="p-8 text-center">
          <div className="flex justify-center mb-3">
            <div className="rounded-full bg-gray-100 dark:bg-gray-700 p-3">
              <ArchiveBoxIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{emptyMessage}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Powers will appear here when available.</p>
        </div>
      ) : filteredPowers.length === 0 && quickAddValue ? (
        <div className="p-8 text-center">
          <div className="flex justify-center mb-3">
            <div className="rounded-full bg-gray-100 dark:bg-gray-700 p-3">
              <MagnifyingGlassIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No powers match your search</p>
          <button
            onClick={() => { setQuickAddValue(''); setQuickAddError(''); }}
            className="text-xs text-primary hover:text-primary-dark dark:text-primary-light mt-1 underline"
          >
            Clear the filter
          </button>
        </div>
      ) : (
        <div className="max-h-60 sm:max-h-72 lg:max-h-96 overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm min-w-0" role="grid" aria-label={title}>
            <thead className="bg-gray-50 dark:bg-gray-700/30 sticky top-0 z-10">
              <tr>
                <th className="w-8 sm:w-10 px-1.5 sm:px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={handleSelectAll}
                    aria-label="Select all powers"
                    className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-1.5 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={handleSortToggle}
                    className="inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    aria-label={`Sort by power number${sortDirection === 'asc' ? ', currently ascending' : sortDirection === 'desc' ? ', currently descending' : ''}`}
                  >
                    Power #
                    {sortDirection === 'asc' && <ChevronUpIcon className="h-3.5 w-3.5" />}
                    {sortDirection === 'desc' && <ChevronDownIcon className="h-3.5 w-3.5" />}
                    {sortDirection === null && <ChevronUpDownIcon className="h-3.5 w-3.5 opacity-40" />}
                  </button>
                </th>
                <th className="hidden sm:table-cell px-1.5 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-1.5 sm:px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredPowers.map((power) => (
                <tr
                  key={power.powerNo}
                  tabIndex={0}
                  role="row"
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/20 cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-primary focus:ring-inset ${
                    selectedIds.has(power.powerNo) ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''
                  }`}
                  onClick={() => handleToggle(power.powerNo)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToggle(power.powerNo);
                    }
                  }}
                >
                  <td className="w-8 sm:w-10 px-1.5 sm:px-3 py-2.5 sm:py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(power.powerNo)}
                      onChange={() => handleToggle(power.powerNo)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select power ${power.powerNo}`}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary h-4 w-4"
                    />
                  </td>
                  <td className="px-1.5 sm:px-3 py-2.5 sm:py-2 font-mono text-gray-900 dark:text-white text-xs sm:text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      {power.powerNo}
                      <ElectronicPowerBadge power={power} powerNumber={power.powerNo} agencyId={agencyId} size="xs" />
                    </span>
                  </td>
                  <td className="hidden sm:table-cell px-1.5 sm:px-3 py-2 text-gray-600 dark:text-gray-300">{power.powerTypeDescription || power.powerTypeCd}</td>
                  <td className="px-1.5 sm:px-3 py-2.5 sm:py-2 text-right text-gray-600 dark:text-gray-300 text-xs sm:text-sm">{formatCurrency(power.powerAmountValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PowerGrid;
